import React, {
  useContext, useEffect, SyntheticEvent, useMemo, useCallback,
} from 'react';
import Snackbar, { SnackbarCloseReason } from '@material-ui/core/Snackbar';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import { useSelector, batch, useDispatch } from 'react-redux';
import styled from 'styled-components';

import useTwilioVideoCall from 'components/twilio/hooks/useTwilioVideoCall';
import { RONGCLOUD_CONVTYPES } from 'components/twilio/constants';
import { UserSessionContext } from 'components/UserSession';
import { setCallStatus } from 'models/rtcCallingStatus';
import generateVideoCallLink from 'utils/generateVideoCallLink';
import {
  selectRtcAlert,
  selectRtcTimer,
  selectRtcWaitingTimeLeft,
  decrementWaitingTime,
  setRtcTimer,
  resetTimer,
  setRtcAlert,
} from 'models/rtcGlobals';

import { setCallInfo } from 'models/rtcCallingInfo';
import { selectLocale } from 'models/localization';
import useMutableRef from 'components/twilio/hooks/useMutableRef';

import RtcChatDesktop from './RtcChatDesktop';
import RtcChatMobile from './RtcChatMobile';
import TimeoutPage from './TimeoutPage';
import UnsupportedBrowserWarning from './UnsupportedBrowserWarning';
import useNotificationAudio from './useNotificationAudio';

const SnackbarContainer = styled.div`
  & > div {
    position: absolute;
    top: 40%;
    z-index: 2000;

    .MuiSnackbarContent-root {
      min-width: fit-content;
    }
  }
`;

export interface RtcChatProps {
  accept: (callInvitationId: any, callInvitationType: any, roomToken: any) => void;
  hangup: (callInvitationId: any, callInvitationType: any, roomToken: any) => void;
  createVideoCall: (roomName: any, chatId: any, inviteeIds: any, inviteeType: any) => void;
  createGroupVideoCall: (name: any, id: any, chatroomId: any) => void;
}

const useVideoCallSwitch = (): RtcChatProps => {
  const dispatch = useDispatch();
  const TwilioVideoCallFunctions = useTwilioVideoCall();
  const waitingTimeLeft = useSelector(selectRtcWaitingTimeLeft);
  const rtcTimer = useSelector(selectRtcTimer);

  const rtcCallingInfo = useSelector((state: any) => state.rtcCallingInfo);
  const callingInfoRef = useMutableRef(rtcCallingInfo);

  const rtcCallingStatus: string = useSelector((state: any) => state.rtcCallingStatus);
  const callingStatusRef = useMutableRef(rtcCallingStatus);

  useEffect(() => {
    if (!rtcTimer && waitingTimeLeft) {
      const timer = setInterval(() => {
        dispatch(decrementWaitingTime());
      }, 1000);
      dispatch(setRtcTimer(timer));
    }
    const { chatId, conversationType, roomToken } = callingInfoRef.current;
    if (!waitingTimeLeft && callingStatusRef.current === 'waiting_for_response' && roomToken) {
      const hangupFunction = TwilioVideoCallFunctions.hangup;
      hangupFunction(
        chatId,
        conversationType,
        roomToken,
        true,
      );
      dispatch(resetTimer());
    }
  }, [dispatch, rtcTimer, waitingTimeLeft, TwilioVideoCallFunctions, callingInfoRef, callingStatusRef]);

  return TwilioVideoCallFunctions;
};

const RtcChatBackdrop = () => {
  const rtcCallingInfo = useSelector((state: any) => state.rtcCallingInfo);
  const rtcCallingStatus: string = useSelector((state: any) => state.rtcCallingStatus);
  const {
    accept, hangup, createVideoCall, createGroupVideoCall,
  } = useVideoCallSwitch();
  const waitingTimeLeft = useSelector(selectRtcWaitingTimeLeft);
  const rtcAlert = useSelector(selectRtcAlert);
  const isDesktop = useMediaQuery('(min-width: 960px)');
  const { play, pause } = useNotificationAudio();

  const { userSession } = useContext(UserSessionContext);
  const { rongcloudUserId } = (userSession || {});
  const locale = useSelector(selectLocale);
  const videoCallUrl = generateVideoCallLink(rtcCallingInfo.roomToken, rongcloudUserId, locale, rtcCallingInfo.boothId);
  const dispatch = useDispatch();
  const handleClose = (event: SyntheticEvent<any, Event>, reason: SnackbarCloseReason) => {
    if (reason === 'clickaway') {
      return;
    }
    dispatch(setRtcAlert(''));
  };

  useEffect(() => {
    if (['waiting_for_response', 'being_called'].includes(rtcCallingStatus)) {
      play(true);
    } else {
      pause();
    }
  }, [rtcCallingStatus, pause, play]);

  const CallingPage = useMemo(() => (isDesktop ? RtcChatDesktop : RtcChatMobile), [isDesktop]);

  const renderBackdrop = useCallback(() => {
    const decline = () => hangup(rtcCallingInfo.chatId, rtcCallingInfo.conversationType, rtcCallingInfo.roomToken);

    const pickUp = () => accept(rtcCallingInfo.chatId, rtcCallingInfo.conversationType, rtcCallingInfo.roomToken);

    const cancel = () => hangup(rtcCallingInfo.chatId, rtcCallingInfo.conversationType, rtcCallingInfo.roomToken);

    const close = () => {
      batch(() => {
        dispatch(setCallStatus('available'));
        dispatch(setCallInfo({}));
      });
    };

    const retryCall = () => {
      if (rtcCallingInfo.conversationType === RONGCLOUD_CONVTYPES.PRIVATE) {
        createVideoCall(rtcCallingInfo.name, rtcCallingInfo.chatId, rtcCallingInfo.calleeIds?.[0], rtcCallingInfo.conversationType);
      } else {
        createGroupVideoCall(rtcCallingInfo.name, rtcCallingInfo.boothId, rtcCallingInfo.chatId);
      }
    };

    const onVideoCallUrlClick = () => {
      window.open(videoCallUrl, '_blank');
    };

    switch (rtcCallingStatus) {
      case 'available':
        return null;
      case 'timeout':
        return (
          <TimeoutPage
            close={close}
            retryCall={retryCall}
          />
        );
      case 'caller_browser_not_support':
      case 'callee_browser_not_support':
      case 'other_side_browser_not_support':
        return (
          <UnsupportedBrowserWarning
            rtcCallingStatus={rtcCallingStatus}
            rtcCallingInfo={rtcCallingInfo}
            close={close}
            isDesktop={isDesktop}
          />
        );
      default:
        return (
          <CallingPage
            rtcCallingInfo={rtcCallingInfo}
            rtcCallingStatus={rtcCallingStatus}
            decline={decline}
            pickUp={pickUp}
            cancel={cancel}
            onVideoCallUrlClick={onVideoCallUrlClick}
            close={close}
            waitingTimeLeft={waitingTimeLeft}
          />
        );
    }
  }, [rtcCallingStatus, hangup, rtcCallingInfo, accept, dispatch, createVideoCall, createGroupVideoCall, isDesktop, videoCallUrl, waitingTimeLeft, CallingPage]);

  return (
    <>
      {
        renderBackdrop()
      }
      <SnackbarContainer>
        <Snackbar
          anchorOrigin={{
            vertical: 'top',
            horizontal: 'center',
          }}
          open={!!rtcAlert}
          autoHideDuration={3000}
          onClose={handleClose}
          message={rtcAlert}
        />
      </SnackbarContainer>
    </>
  );
};

export default RtcChatBackdrop;
